import analytics from "core/utils/analytics";
import { postMessage } from "core/utils/tracker";

/**
 * Воронка эмбеда и рекламы в нём.
 *
 * Зачем: по жалобам на слабый fill rate невозможно понять, где теряются
 * показы, потому что наружу видно только импрешены. Здесь каждый шаг
 * фиксируется отдельным событием, и разрез `surface = embed` (его ставит
 * player/utils/setEmbedAnalyticsContext) позволяет смотреть воронку
 * только по эмбеду:
 *
 *   embed_mounted → embed_content_loaded → embed_in_view
 *     → view_created
 *       → ad_requested → ad_filled | ad_no_fill
 *         → ad_load_started → ad_loaded | ad_load_failed
 *           → ad_impression → ad_firstquartile → ad_midpoint
 *             → ad_thirdquartile → ad_complete
 *
 * Каждое расхождение между соседними шагами это ответ на вопрос, куда
 * делись показы: `view_created` против `embed_in_view` показывает, сколько
 * зрителей вообще нажали play, нет `ad_requested` при живых `view_created`
 * значит плеер не дошёл до запроса, много `ad_no_fill` значит не отвечает
 * кампания, `ad_load_failed` после `ad_filled` значит креатив есть, но не
 * грузится, а разрыв между `ad_loaded` и `ad_impression` это уже
 * блокировщики и autoplay.
 *
 * Дублирование в postMessage сохранено: встраивающая страница получает
 * те же события, что и раньше, ломать её контракт нельзя.
 */
export const EMBED_MOUNTED = "embed_mounted";
export const EMBED_CONTENT_LOADED = "embed_content_loaded";
export const EMBED_CONTENT_FAILED = "embed_content_failed";
export const EMBED_IN_VIEW = "embed_in_view";
export const EMBED_OUT_OF_VIEW = "embed_out_of_view";
export const VIEW_CREATED = "view_created";

export const AD_REQUESTED = "ad_requested";
export const AD_FILLED = "ad_filled";
export const AD_NO_FILL = "ad_no_fill";
export const AD_LOAD_STARTED = "ad_load_started";
export const AD_LOADED = "ad_loaded";
export const AD_LOAD_FAILED = "ad_load_failed";

const EMBED = "Embed";
const AD = "Ad";

/**
 * Шаг воронки эмбеда: загрузка, видимость, создание просмотра.
 */
export function trackEmbedEvent(action, params = {}) {
  analytics.track(action, { category: EMBED, ...params });
}

/**
 * Шаг воронки рекламы. `place` это место показа (PREROLL_IN_VIDEO_IN_EMBED
 * и подобные), `creative` тип креатива, чтобы отделить VAST от Яндекса и
 * баннеров Adfox.
 *
 * `silent` отключает postMessage для шагов, которых наружу раньше не было:
 * встраивающая страница не должна получать новые типы событий неожиданно.
 */
export function trackAdEvent(action, params = {}) {
  const { silent, creative, place, ...rest } = params;

  if (!silent) {
    postMessage({
      eventName: action,
      creative: creative || "SponsorVideo",
      place,
    });
  }

  analytics.track(action, { category: AD, creative, place, ...rest });
}

/**
 * Событие самого креатива: IMPRESSION, START, FIRSTQUARTILE, SKIP, ERROR и
 * прочие имена из VAST. Приводим к общему виду `ad_impression`, чтобы
 * четыре формата креативов (наше видео, VAST, Яндекс, баннер Adfox)
 * ложились в один отчёт и сравнивались между собой.
 *
 * По умолчанию `silent`: наружу, во встраивающую страницу, эти события уже
 * отправляет сам компонент креатива, дублировать не надо.
 */
export function trackAdCreativeEvent(event, params = {}) {
  if (!event) return;

  trackAdEvent(`ad_${String(event).toLowerCase()}`, {
    silent: true,
    ...params,
  });
}
